import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { cn } from "@utils/tw"
import { EditableText, usePostTitleEditor } from "eddev/blocks"
import { Link } from "eddev/routing"
import { InsightCategoryFragment, ResponsiveImageFragment } from "../../types.graphql"
import { Button } from "@components/atoms/Button"
import dayjs from "dayjs"
import { useIntersectionObserver } from "usehooks-ts"
import { Appear } from "@components/motion/Appear"

export const InsightHeader = ({
  title,
  type,
  typeSlug,
  typeUri,
  categories,
  displayedDate,
  headline,
  featuredImage,
  personnel,
  formId,
}: {
  title: string
  type: string
  typeSlug: string
  typeUri: string
  categories: (InsightCategoryFragment | null)[]
  displayedDate: string
  headline?: string | null
  featuredImage: ResponsiveImageFragment | null
  personnel?:
    | ({
        name: string | null
        jobTitle: string | null
        image: ResponsiveImageFragment | null
        link: {
          url: string | null
          title: string | null
          target: string | null
        } | null
      } | null)[]
    | null
  formId?: string | null
}) => {
  const { isIntersecting, ref } = useIntersectionObserver({
    root: null,
    rootMargin: "0px",
    threshold: 0.1,
  })
  const isArticle = !["reports", "webinars"].includes(typeSlug)
  const isWebinar = typeSlug === "webinars"
  const isReport = typeSlug === "reports"
  const isFuture = dayjs(displayedDate).isAfter(dayjs())

  const scrollToForm = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    if (formId) {
      const element = document.getElementById(`register-${formId}`)
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }
  }

  return (
    <div
      ref={ref}
      className={cn(
        "w-full content-sized mt-5 mb-9 md:mb-7 flex flex-col gap-8 md:gap-10",
        isArticle && "md:w-cols-8 md:mx-auto",
      )}
    >
      <div className={cn("w-full grid grid-cols-1 gap-9 md:gap-7", !isArticle && "md:grid-cols-12")}>
        <div
          className={cn(
            "flex flex-col gap-6",
            !isArticle && "md:col-span-6 md:gap-5 flex-col-reverse md:justify-center",
          )}
        >
          {!isArticle && (
            <Appear active={isIntersecting} activeOnly delay={150} className="w-full flex flex-col md:flex-col gap-5">
              {headline && <p className="type-serif-lg-regular md:type-serif-xl-regular">{headline}</p>}
              {isWebinar && formId && (
                <Button
                  variant={isFuture ? "orange" : "dark"}
                  size="sm"
                  href={`#register-${formId}`}
                  onClick={(e) => scrollToForm(e)}
                >
                  {isFuture ? "Register for the Webinar" : "Watch the Recording"}
                </Button>
              )}
              {isReport && formId && (
                <Button variant="dark" size="sm" href={`#register-${formId}`} onClick={(e) => scrollToForm(e)}>
                  Download the Report
                </Button>
              )}
            </Appear>
          )}
          <div className="flex flex-col gap-6">
            <Appear
              as="h1"
              slide
              active={isIntersecting}
              activeOnly
              className={cn(
                "type-sans-2-xl-semibold md:type-sans-3-xl-semibold",
                !isArticle && "md:type-sans-2-xl-semibold",
              )}
            >
              {env.admin ? (
                <EditableText as="span" store={usePostTitleEditor()} className="" placeholder="Enter a title" />
              ) : (
                title
              )}
            </Appear>
          </div>
          <Appear active={isIntersecting} activeOnly className="flex gap-4 flex-wrap items-center type-mono-sm-regular">
            <Link href={typeUri} className="uppercase hover-link">
              {type}
            </Link>
            {categories.length > 0 && isArticle && (
              <div className="flex gap-4 flex-wrap">
                {categories.map((category, index) => {
                  if (!category) return null
                  return (
                    <Link
                      key={index}
                      href={category.uri || "#"}
                      className="before:content-['#'] before:mr-0.5 uppercase hover-link"
                    >
                      {category.name}
                    </Link>
                  )
                })}
              </div>
            )}
            {displayedDate && <p className="uppercase">{displayedDate}</p>}
          </Appear>
        </div>
        <div className={cn("", !isArticle && "md:col-span-5 md:col-start-8")}>
          {(isArticle || isReport || (isWebinar && !personnel)) && (
            <Appear active={isIntersecting} activeOnly delay={150}>
              <FeaturedImage featuredImage={featuredImage} />
            </Appear>
          )}

          {isWebinar && personnel && (
            <Appear active={isIntersecting} activeOnly className="w-full flex flex-col gap-4">
              <EditableText
                store="webinarHostTitle"
                as="p"
                className="type-sans-lg-semibold"
                placeholder="Meet the experts"
                defaultValue="Meet the experts"
              />
              <div className="w-full flex flex-col gap-4">
                {personnel.map((person, index) => {
                  const El = person?.link?.url ? Link : "div"
                  const elProps = person?.link?.url
                    ? { href: person.link.url, target: person.link.target || undefined }
                    : {}
                  return (
                    !!person && (
                      <Appear key={index} active={isIntersecting} activeOnly delay={150 * index} className="w-full">
                        <El
                          {...elProps}
                          className={cn(
                            "w-full flex gap-4 items-center rounded-md bg-slate50 p-2",
                            person.link?.url && "hover:bg-slate300 transition-all duration-300",
                          )}
                        >
                          {person.image?.mediaItemUrl && (
                            <div className="w-11 h-11 rounded-sm overflow-hidden shrink-0">
                              <ResponsiveImage {...person.image} className="w-full h-full" objectFit="cover" />
                            </div>
                          )}
                          <div className="flex flex-col self-stretch justify-between gap-4">
                            <p className="type-serif-lg-regular">{person.name}</p>
                            <p className="type-mono-sm-regular whitespace-pre-line">{person.jobTitle}</p>
                          </div>
                        </El>
                      </Appear>
                    )
                  )
                })}
              </div>
            </Appear>
          )}
        </div>
      </div>
      {isWebinar && personnel && featuredImage && (
        <Appear active={isIntersecting} activeOnly delay={150}>
          <div className="w-full md:w-cols-6 mx-auto">
            <FeaturedImage featuredImage={featuredImage} />
          </div>
        </Appear>
      )}
    </div>
  )
}

const FeaturedImage = ({ featuredImage }: { featuredImage: ResponsiveImageFragment | null }) => {
  return (
    <div className="w-full rounded-md overflow-hidden">
      {featuredImage?.mediaItemUrl ? (
        <ResponsiveImage {...featuredImage} className="w-full h-auto" />
      ) : (
        env.admin && (
          <div className="w-full p-5 bg-slate300 flex items-center justify-center text-slate950 type-sans-base-regular">
            Featured image not set. Please set a featured image to display here.
          </div>
        )
      )}
    </div>
  )
}
